import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
import { TranslationService } from '@app/core/_base/layout';
import { forkJoin, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  AdminBlogCategory,
  AdminBlogPost,
  AdminBlogTag,
  BlogActionResponse,
  BlogCategoryCreateRequest,
  BlogCategoryEditData,
  BlogCategoryLanguageContent,
  BlogCategoryUpdateRequest,
  BlogPostCreateRequest,
  BlogPostEditData,
  BlogPostLanguageContent,
  BlogPostUpdateRequest,
  BlogTagCreateRequest,
  BlogTagEditData,
  BlogTagLanguageContent,
  BlogTagUpdateRequest
} from '@app/model/blog-management.model';
import { take, takeUntil } from 'rxjs/operators';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface ApiCollection<T> {
  collection: T[];
  total: number;
}

interface ApiBlogPostReference {
  id: string;
  slug: string;
}

interface ApiBlogCategoryPostReference {
  id: string;
  title: string;
  order: number;
}

interface ApiBlogCategoryChildReference {
  id: string;
  name: string;
}

interface ApiBlogCategoryTranslation {
  language_code: string;
  name: string;
  slug: string;
}

interface ApiBlogCategory {
  id: string;
  code: string;
  name: string;
  slug: string;
  parent_id: string | null;
  priority: number | null;
  is_hide: boolean;
  created_at: string;
  modified_at: string | null;

  img?: string | null;
  image?: string | null;
  description?: string | null;

  total_blog?: number;
  post_count?: number;

  blog_posts?: ApiBlogCategoryPostReference[];
  blog_post?: ApiBlogPostReference[];

  category_child_total?: number;
  category_child?: ApiBlogCategoryChildReference[];

  translations?: ApiBlogCategoryTranslation[];
}

interface ApiBlogPostCategoryReference {
  id: string;
  name: string;
}

interface ApiBlogPostTranslationReference {
  language_code: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
}

interface ApiBlogPost {
  id: string;

  category_id?: string;
  category?: ApiBlogPostCategoryReference;
  category_detail?: ApiBlogCategory;

  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image: string | null;
  author: string | null;
  view_count: number;

  is_published: boolean;
  is_hot: boolean;
  is_featured: boolean;

  published_at: string | null;
  created_at: string;
  modified_at?: string | null;

  tags?: Array<ApiBlogTag | string>;
  translations?: ApiBlogPostTranslationReference[];
  related_posts?: ApiBlogPostReference[];
}

interface ApiBlogCategoryCollection {
  collection: ApiBlogCategory[];
  total: number;
}

interface ApiBlogTagTranslation {
  language_code: string;
  name: string;
  slug: string;
}

interface ApiBlogTag {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  translations: ApiBlogTagTranslation[];
}

@Injectable({
  providedIn: 'root'
})
export class BlogManagementDataService {
  private readonly apiUrl = environment.backendhost + '/api/blog';

  constructor(
    private http: HttpClient,
    private translationService: TranslationService
  ) {}

  getAllPosts(): Observable<AdminBlogPost[]> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) => this.loadAllPosts(language))
    );
  }

  private getPostCategoryId(post: ApiBlogPost): string {
    if (post.category && post.category.id) {
      return post.category.id;
    }

    if (post.category_id) {
      return post.category_id;
    }

    if (post.category_detail && post.category_detail.id) {
      return post.category_detail.id;
    }

    return '';
  }

  //   getCategories(): Observable<AdminBlogCategory[]> {
  //     return this.translationService.language$.pipe(
  //       map((language) => this.mapApiLanguage(language)),
  //       distinctUntilChanged(),
  //       switchMap((language) =>
  //         this.http.get<ApiResponse<ApiBlogCategoryCollection>>(
  //           this.apiUrl + '/categories?language=' + encodeURIComponent(language)
  //         )
  //       ),
  //       map((response) =>
  //         response.data.collection
  //           .map((category) => ({
  //             id: category.id,
  //             code: category.code || '',
  //             name: category.name || '',
  //             slug: category.slug || '',
  //             description: category.description || '',
  //             image: category.img || category.image || null,
  //             parentId: category.parent_id || null,
  //             postCount:
  //               category.total_blog !== undefined ? category.total_blog : category.post_count || 0,
  //             childCount:
  //               category.category_child_total !== undefined
  //                 ? category.category_child_total
  //                 : (category.category_child || []).length,
  //             priority:
  //               category.priority !== null && category.priority !== undefined ? category.priority : 0,
  //             isHidden: category.is_hide === true,
  //             createdAt: category.created_at,
  //             modifiedAt: category.modified_at || null
  //           }))
  //           .sort(
  //             (firstCategory, secondCategory) =>
  //               firstCategory.priority - secondCategory.priority ||
  //               firstCategory.name.localeCompare(secondCategory.name)
  //           )
  //       )
  //     );
  //   }

  getCategories(): Observable<AdminBlogCategory[]> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) =>
        this.http.get<ApiResponse<ApiBlogCategoryCollection>>(
          this.apiUrl + '/categories?language=' + encodeURIComponent(language)
        )
      ),
      map((response) => {
        const categoryData: any = response ? response.data : null;

        const categoryCollection: ApiBlogCategory[] =
          categoryData && Array.isArray(categoryData.collection)
            ? categoryData.collection
            : Array.isArray(categoryData)
              ? categoryData
              : [];

        return categoryCollection
          .map((category) => ({
            id: category.id,
            code: category.code || '',
            name: category.name || '',
            slug: category.slug || '',
            description: category.description || '',
            image: category.img || category.image || null,
            parentId: category.parent_id || null,
            postCount:
              category.total_blog !== undefined ? category.total_blog : category.post_count || 0,
            childCount:
              category.category_child_total !== undefined
                ? category.category_child_total
                : (category.category_child || []).length,
            priority:
              category.priority !== null && category.priority !== undefined ? category.priority : 0,
            isHidden: category.is_hide === true,
            createdAt: category.created_at,
            modifiedAt: category.modified_at || null
          }))
          .sort(
            (firstCategory, secondCategory) =>
              firstCategory.priority - secondCategory.priority ||
              firstCategory.name.localeCompare(secondCategory.name)
          );
      })
    );
  }

  createCategory(payload: BlogCategoryCreateRequest): Observable<BlogActionResponse> {
    return this.http.post<BlogActionResponse>(this.apiUrl + '/categories', payload);
  }

  updateCategory(
    categoryId: string,
    payload: BlogCategoryUpdateRequest
  ): Observable<BlogActionResponse> {
    return this.http.put<BlogActionResponse>(
      this.apiUrl + '/categories/' + encodeURIComponent(categoryId),
      payload
    );
  }

  uploadCategoryImage(categoryId: string, imageFile: File): Observable<BlogActionResponse> {
    const formData = new FormData();

    formData.append('file', imageFile);

    return this.http.put<BlogActionResponse>(
      this.apiUrl + '/categories/' + encodeURIComponent(categoryId) + '/upload-image',
      formData
    );
  }

  deleteCategory(categoryId: string): Observable<BlogActionResponse> {
    return this.http.delete<BlogActionResponse>(
      this.apiUrl + '/categories/' + encodeURIComponent(categoryId)
    );
  }

  hasPostsInCategory(categorySlug: string): Observable<boolean> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      take(1),
      switchMap((language) =>
        this.http.get<ApiResponse<ApiCollection<ApiBlogPost>>>(
          this.apiUrl +
            '/posts?category=' +
            encodeURIComponent(categorySlug) +
            '&language=' +
            encodeURIComponent(language) +
            '&Page=1&PageSize=1'
        )
      ),
      map((response) => response.data.total > 0)
    );
  }

  getTags(): Observable<AdminBlogTag[]> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged(),
      switchMap((language) =>
        this.http.get<ApiResponse<ApiBlogTag[]>>(this.apiUrl + '/tags').pipe(
          map((response) => ({
            language: language,
            tags: response.data || []
          }))
        )
      ),
      map((result) =>
        result.tags
          .map((tag) => {
            const displayContent = this.getTagDisplayContent(tag, result.language);

            return {
              id: tag.id,
              name: displayContent.name,
              slug: displayContent.slug,
              createdAt: tag.created_at,
              translations: tag.translations || []
            };
          })
          .sort((firstTag, secondTag) =>
            firstTag.name.localeCompare(secondTag.name, result.language)
          )
      )
    );
  }

  getTagForEdit(tagId: string): Observable<BlogTagEditData> {
    return this.http
      .get<ApiResponse<ApiBlogTag>>(this.apiUrl + '/tags/' + encodeURIComponent(tagId))
      .pipe(
        map((response) => {
          const tag = response.data;

          return {
            id: tag.id,
            languageContents: {
              vi: this.getTagLanguageContent(tag, 'vi'),
              en: this.getTagLanguageContent(tag, 'en'),
              zh: this.getTagLanguageContent(tag, 'zh')
            }
          };
        })
      );
  }

  createTag(payload: BlogTagCreateRequest): Observable<BlogActionResponse> {
    return this.http.post<BlogActionResponse>(this.apiUrl + '/tags', payload);
  }

  updateTag(tagId: string, payload: BlogTagUpdateRequest): Observable<BlogActionResponse> {
    return this.http.put<BlogActionResponse>(
      this.apiUrl + '/tags/' + encodeURIComponent(tagId),
      payload
    );
  }

  deleteTag(tagId: string): Observable<BlogActionResponse> {
    return this.http.delete<BlogActionResponse>(this.apiUrl + '/tags/' + encodeURIComponent(tagId));
  }

  createPost(payload: BlogPostCreateRequest): Observable<BlogActionResponse> {
    return this.http.post<BlogActionResponse>(this.apiUrl + '/posts', payload);
  }

  getCategoryForEdit(category: AdminBlogCategory): Observable<BlogCategoryEditData> {
    return forkJoin({
      vi: this.getCategoryByIdAndLanguage(category.id, 'vi'),
      en: this.getCategoryByIdAndLanguage(category.id, 'en'),
      zh: this.getCategoryByIdAndLanguage(category.id, 'zh')
    }).pipe(
      map(({ vi, en, zh }) => {
        const categoryDetail = vi || en || zh;

        if (!categoryDetail) {
          throw new Error('Category not found');
        }

        return {
          id: categoryDetail.id,
          code: categoryDetail.code || '',
          image: categoryDetail.img || categoryDetail.image || '',
          parentId: categoryDetail.parent_id || null,
          priority:
            categoryDetail.priority !== null && categoryDetail.priority !== undefined
              ? categoryDetail.priority
              : 0,
          isHidden: categoryDetail.is_hide === true,
          languageContents: {
            vi: this.mapCategoryLanguageContent(vi),
            en: this.mapCategoryLanguageContent(en),
            zh: this.mapCategoryLanguageContent(zh)
          }
        };
      })
    );
  }

  private getCategoryByIdAndLanguage(
    categoryId: string,
    language: string
  ): Observable<ApiBlogCategory | null> {
    const url = this.apiUrl + '/categories?language=' + encodeURIComponent(language);

    return this.http.get<ApiResponse<ApiBlogCategoryCollection>>(url).pipe(
      map((response) => {
        const categoryData: any = response && response.data ? response.data : null;

        const categories: ApiBlogCategory[] =
          categoryData && Array.isArray(categoryData.collection)
            ? categoryData.collection
            : Array.isArray(categoryData)
              ? categoryData
              : [];

        return categories.find((item) => item.id === categoryId) || null;
      })
    );
  }

  private mapCategoryLanguageContent(
    category: ApiBlogCategory | null
  ): BlogCategoryLanguageContent {
    if (!category) {
      return {
        name: '',
        slug: ''
      };
    }

    return {
      name: category.name || '',
      slug: category.slug || ''
    };
  }

  getPostForEdit(post: AdminBlogPost): Observable<BlogPostEditData> {
    return this.getPostForEditById(post.id).pipe(
      map((postDetail) => ({
        id: postDetail.id,
        categoryId: this.getPostCategoryId(postDetail),
        tagIds: this.getPostTagIds(postDetail),
        featuredImage: postDetail.featured_image || '',
        author: postDetail.author || '',
        isPublished: postDetail.is_published === true,
        isHot: postDetail.is_hot === true,
        isFeatured: postDetail.is_featured === true,
        publishedAt: postDetail.published_at,
        languageContents: {
          vi: this.getPostTranslationContent(postDetail, 'vi'),
          en: this.getPostTranslationContent(postDetail, 'en'),
          zh: this.getPostTranslationContent(postDetail, 'zh')
        }
      }))
    );
  }

  private getPostForEditById(postId: string): Observable<ApiBlogPost> {
    return forkJoin({
      publishedPosts: this.getPostsByPublishedStatus(true),
      unpublishedPosts: this.getPostsByPublishedStatus(false)
    }).pipe(
      map(({ publishedPosts, unpublishedPosts }) => {
        const postDetail = publishedPosts
          .concat(unpublishedPosts)
          .find((item) => item.id === postId);

        if (!postDetail) {
          throw new Error('Post not found');
        }

        return postDetail;
      })
    );
  }

  private getPostsByPublishedStatus(isPublished: boolean): Observable<ApiBlogPost[]> {
    const url =
      this.apiUrl + '/posts?Page=1&PageSize=9999' + '&is_published=' + isPublished + '&language=en';

    return this.http.get<ApiResponse<ApiCollection<ApiBlogPost>>>(url).pipe(
      map((response) => {
        if (
          !response ||
          !response.success ||
          !response.data ||
          !Array.isArray(response.data.collection)
        ) {
          return [];
        }

        return response.data.collection;
      })
    );
  }

  updatePost(postId: string, payload: BlogPostUpdateRequest): Observable<BlogActionResponse> {
    return this.http.put<BlogActionResponse>(
      this.apiUrl + '/posts/' + encodeURIComponent(postId),
      payload
    );
  }

  uploadPostImage(postId: string, imageFile: File): Observable<BlogActionResponse> {
    const formData = new FormData();

    formData.append('file', imageFile);

    return this.http.put<BlogActionResponse>(
      this.apiUrl + '/posts/' + encodeURIComponent(postId) + '/upload-image',
      formData
    );
  }

  deletePost(postId: string): Observable<BlogActionResponse> {
    return this.http.delete<BlogActionResponse>(
      this.apiUrl + '/posts/' + encodeURIComponent(postId)
    );
  }

  private getPostTagIds(post: ApiBlogPost): string[] {
    const tags = post.tags || [];

    return tags.filter((tag): tag is ApiBlogTag => typeof tag !== 'string').map((tag) => tag.id);
  }

  private getPostTranslationContent(
    post: ApiBlogPost,
    languageCode: string
  ): BlogPostLanguageContent {
    const translation = (post.translations || []).find(
      (item) => item.language_code === languageCode
    );

    if (!translation) {
      return {
        title: '',
        slug: '',
        excerpt: '',
        content: ''
      };
    }

    return {
      title: translation.title || '',
      slug: translation.slug || '',
      excerpt: translation.excerpt || '',
      content: translation.content || ''
    };
  }

  private getPostDetail(slug: string, language: string): Observable<ApiBlogPost> {
    const url =
      this.apiUrl +
      '/posts/' +
      encodeURIComponent(slug) +
      '?language=' +
      encodeURIComponent(language);

    return this.http.get<ApiResponse<ApiBlogPost>>(url).pipe(map((response) => response.data));
  }

  private mapLanguageContent(post: ApiBlogPost): BlogPostLanguageContent {
    return {
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || ''
    };
  }

  private loadAllPosts(language: string): Observable<AdminBlogPost[]> {
    const languageQuery = encodeURIComponent(language);

    return forkJoin({
      postsResponse: this.http.get<ApiResponse<ApiCollection<ApiBlogPost>>>(
        this.apiUrl + '/posts?language=' + languageQuery + '&Page=1&PageSize=9999'
      ),

      categoriesResponse: this.http.get<ApiResponse<ApiBlogCategoryCollection>>(
        this.apiUrl + '/categories?language=' + languageQuery
      )
    }).pipe(
      map((response) => {
        const categoryNames: {
          [categoryId: string]: string;
        } = {};

        response.categoriesResponse.data.collection.forEach((category) => {
          categoryNames[category.id] = category.name || '';
        });

        return response.postsResponse.data.collection
          .sort(
            (firstPost, secondPost) => this.getPostTime(secondPost) - this.getPostTime(firstPost)
          )
          .map((post) => {
            const categoryId = this.getPostCategoryId(post);

            return this.mapPost(post, categoryNames[categoryId]);
          });
      })
    );
  }

  private mapPost(post: ApiBlogPost, localizedCategoryName = ''): AdminBlogPost {
    const categoryId = this.getPostCategoryId(post);

    return {
      id: post.id,
      categoryId: categoryId,
      categoryName:
        localizedCategoryName ||
        (post.category ? post.category.name : '') ||
        (post.category_detail ? post.category_detail.name : ''),
      title: post.title || '',
      slug: post.slug || '',
      originalSlug: this.getOriginalPostSlug(post),
      excerpt: post.excerpt || '',
      content: post.content || '',
      featuredImage: this.getImageUrl(post.featured_image),
      author: post.author || '',
      viewCount: post.view_count || 0,
      isPublished: post.is_published === true,
      isHot: post.is_hot === true,
      isFeatured: post.is_featured === true,
      publishedAt: post.published_at,
      createdAt: post.created_at
    };
  }

  private getOriginalPostSlug(post: ApiBlogPost): string {
    const translations = post.translations || [];

    const englishTranslation = translations.find(
      (translation) => translation.language_code === 'en'
    );

    if (englishTranslation && englishTranslation.slug) {
      return englishTranslation.slug;
    }

    if (post.category_detail && post.category_detail.blog_post) {
      const originalPost = post.category_detail.blog_post.find((item) => item.id === post.id);

      if (originalPost && originalPost.slug) {
        return originalPost.slug;
      }
    }

    return post.slug || '';
  }

  private getPostTime(post: ApiBlogPost): number {
    return new Date(post.published_at || post.created_at).getTime();
  }

  private getImageUrl(imageUrl: string | null): string {
    if (!imageUrl) {
      return '';
    }

    if (imageUrl.indexOf('http') === 0) {
      return imageUrl;
    }

    return environment.backendhost + imageUrl;
  }

  private getTagLanguageContent(tag: ApiBlogTag, languageCode: string): BlogTagLanguageContent {
    const translations = tag.translations || [];

    const translation = translations.find((item) => item.language_code === languageCode);

    if (translation) {
      return {
        name: translation.name || '',
        slug: translation.slug || ''
      };
    }

    if (languageCode === 'en') {
      return {
        name: tag.name || '',
        slug: tag.slug || ''
      };
    }

    return {
      name: '',
      slug: ''
    };
  }

  private getTagDisplayContent(tag: ApiBlogTag, languageCode: string): BlogTagLanguageContent {
    const translations = tag.translations || [];

    const selectedTranslation = translations.find(
      (translation) => translation.language_code === languageCode
    );

    const vietnameseTranslation = translations.find(
      (translation) => translation.language_code === 'vi'
    );

    let selectedName = '';
    let selectedSlug = '';

    if (selectedTranslation) {
      selectedName = selectedTranslation.name || '';
      selectedSlug = selectedTranslation.slug || '';
    }

    if (languageCode === 'en') {
      selectedName = selectedName || tag.name || '';
      selectedSlug = selectedSlug || tag.slug || '';
    }

    if (vietnameseTranslation) {
      selectedName = selectedName || vietnameseTranslation.name || '';

      selectedSlug = selectedSlug || vietnameseTranslation.slug || '';
    }

    return {
      name: selectedName || tag.name || '',
      slug: selectedSlug || tag.slug || ''
    };
  }

  private mapApiLanguage(language: string): string {
    const currentLanguage = (language || '').toLowerCase();

    if (currentLanguage === 'ch' || currentLanguage === 'cn') {
      return 'zh';
    }

    if (currentLanguage === 'en') {
      return 'en';
    }

    return 'vi';
  }
}
